import {
    Avatar,
    Badge, Button, LegacyCard, DatePicker,
    Frame,
    Layout, List, Modal,
    Page, Pagination, ResourceItem, ResourceList, LegacyStack, Text,
} from "@shopify/polaris";
import {useCallback, useEffect, useState} from "react";
import Api from "../../helpers/api";
import {formatDate, formatDateToUsFormat} from "../Dates";
import {ResourcePicker, useAppBridge, useToast} from "@shopify/app-bridge-react";

const ScheduleProductModal = ({
                                  active = false,
                                  onClose = (reload = false) => {},
}) => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [pickerActive, setPickerActive] = useState(false);
    const [{month, year}, setDate] = useState({month: (new Date()).getMonth(), year: (new Date()).getFullYear()});
    const [selectedDates, setSelectedDates] = useState({
        start: new Date(),
        end: new Date(),
    });
    const {show} = useToast();

    const handleMonthChange = useCallback(
        (month, year) => setDate({month, year}),
        []
    );

    const showToast = (message) => {
        show(message, {
            duration: 5000,
        });
    }

    const onInternalClose = useCallback((reload = false) => {
        if (loading) {
            return;
        }

        setProducts([]);
        setSelectedDates({
            start: new Date(),
            end: new Date(),
        });
        onClose(reload);
    }, [loading]);

    const unpublishProducts = useCallback(() => {
        setLoading(true);
        Api.saveUnpublishments({
            products: products,
            unpublish_at: formatDate(selectedDates.start),
        })
            .then(() => onInternalClose(true))
            .catch((error) => {
                if (error.response.status == 403) {
                    showToast("You don't have permissions to do that");
                } else if (error.response.status == 404) {
                    showToast("Schedule not found");
                } else {
                    showToast("Server error, try again.");
                }
            })
            .finally(() => {
                setLoading(false);
            })
    }, [products, selectedDates]);

    return (
        <Modal
            open={active}
            onClose={() => onInternalClose()}
            title="Schedule new product unpublishment"
            primaryAction={{
                content: 'Unpublish',
                onAction: unpublishProducts,
                loading: loading
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => onInternalClose(),
                    loading: loading
                }
            ]}
        >
            <Modal.Section>
                <DatePicker
                    month={month}
                    year={year}
                    onChange={setSelectedDates}
                    onMonthChange={handleMonthChange}
                    selected={selectedDates}
                />
            </Modal.Section>
            <Modal.Section>
                <LegacyStack alignment={'trailing'}>
                    <Button onClick={() => setPickerActive(true)}>Select products</Button>
                </LegacyStack>
                <ResourcePicker
                    showVariants={false}
                    resourceType={"Product"}
                    open={pickerActive}
                    onCancel={() => setPickerActive(false)}
                    onSelection={(selection) => {
                        setProducts(selection.selection);
                        setPickerActive(false);
                    }}
                    selectMultiple
                    showHidden={false}
                    showDraft={false}
                    showArchived={false}
                    initialSelectionIds={products.map((item) => {
                        return {
                            id: item.id,
                        };
                    })}
                />
                <ResourceList
                    items={products}
                    renderItem={(item) => <ResourceItem
                        id={item.id}
                        media={
                            <Avatar
                                size="md"
                                name={item.title}
                                source={item.images && item.images.length ? item.images[0].originalSrc : null}
                            />
                        }
                    >
                        <Text as={'p'} variation={"strong"}>{item.title}</Text>
                    </ResourceItem>}
                />
            </Modal.Section>
        </Modal>
    );
}

const ScheduleList = () => {
    const {show} = useToast();
    const [loading, setLoading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [modalActive, setModalActive] = useState(false);

    const [data, setData] = useState({
        data: [],
        meta: {
            current_page: 1,
            last_page: 1,
        }
    });

    useEffect(() => {
        loadData();
    }, []);

    const showToast = (message) => {
        show(message, {
            duration: 5000,
        });
    }

    const loadData = useCallback((page = 1) => {
        setLoading(true);

        Api.getScheduledProducts({
            page: page,
        })
            .then(response => {
                setData(response.data);
            })
            .catch((error) => {
                if (error.response.status == 403) {
                    showToast("You don't have permissions to do that");
                } else {
                    showToast("Server error, try again.");
                }
            })
            .finally(() => {
                setLoading(false);
            })
    }, []);

    const removeSchedule = useCallback((id) => {
        setRemoving(true);
        Api
            .removeUnpublishments(id)
            .then(() => {
                showToast("Schedule removed");
                setRemoving(false);
                loadData();
            })
            .catch((error) => {
                if (error.response.status == 403) {
                    showToast("You don't have permissions to do that");
                } else if (error.response.status == 404) {
                    showToast("Schedule not found");
                } else {
                    showToast("Server error, try again.");
                }

                setRemoving(false);
            });
    }, []);

    const onModalClose = useCallback((reload = false) => {
        setModalActive(false);

        if (reload) {
            loadData();
        }
    }, []);

    return (
        <Frame>
            <Page title={`Scheduled Unpublishments`} primaryAction={{
                content: "Add Schedule",
                onAction() {
                    setModalActive(true);
                }
            }}>
                <Layout sectioned>
                    <LegacyCard>
                        <LegacyCard.Section>
                            <ResourceList
                                items={data.data}
                                loading={loading}
                                renderItem={(item) => {
                                    const actions = [];

                                    if (!item.unpublished) {
                                        actions.push({
                                            content: "Remove",
                                            loading: removing,
                                            onClick: () => removeSchedule(item.id)
                                        })
                                    }

                                    return (
                                        <ResourceItem
                                            id={item.id}
                                            shortcutActions={actions}
                                            persistActions={!!actions.length}
                                        >
                                            <LegacyStack>
                                                <>
                                                    <Text as={'p'} variation={"strong"}>Unpublish at:</Text> { formatDateToUsFormat(item.unpublish_at) }</>
                                                <Badge size={"medium"} tone={item.unpublished ? "success" : "info"}>{item.unpublished ? "Unpublished" : "In Queue"}</Badge>
                                            </LegacyStack>
                                            <List>
                                                {item.products.map((product) => <List.Item>
                                                    {product.title}
                                                </List.Item>)}
                                            </List>
                                        </ResourceItem>
                                    );
                                }}
                            />
                        </LegacyCard.Section>
                        <LegacyCard.Section>
                            <Pagination
                                hasNext={data.meta.current_page < data.meta.last_page}
                                hasPrevious={data.meta.current_page > 1}
                                onNext={() => loadData(data.meta.current_page+1)}
                                onPrevious={() => loadData(data.meta.current_page-1)}
                            />
                        </LegacyCard.Section>
                    </LegacyCard>
                </Layout>
                <ScheduleProductModal onClose={onModalClose} active={modalActive} />
            </Page>
        </Frame>
    );
}

export default ScheduleList;
